import { LinkType, type StoryblokLinkBlok } from '@/features/storyblok/types';

const URL_SCHEME_REGEX = /^[a-z][a-z\d+\-.]*:/i;
const HTTP_URL_REGEX = /^https?:\/\//i;

export type StoryblokNavigationTarget =
  | { type: 'internal'; path: string }
  | { type: 'storyblok'; slug: string }
  | { type: 'external'; href: string };

function getBasePath(): string {
  const basePath = (import.meta.env.BASE_URL || '/').trim();
  const prefixedBasePath = basePath.startsWith('/') ? basePath : `/${basePath}`;
  return prefixedBasePath.endsWith('/') ? prefixedBasePath : `${prefixedBasePath}/`;
}

function normalizeInternalPath(rawUrl: string): string {
  const basePath = getBasePath();
  const absoluteUrlMatch = rawUrl.match(/^(?:https?:)?\/\/[^/]+(\/.*)?$/i);
  const hostLikeUrlMatch = rawUrl.match(/^[^/\s]+\.[^/\s]+(\/.*)?$/);

  if (absoluteUrlMatch || hostLikeUrlMatch) {
    const extractedPath = absoluteUrlMatch?.[1] ?? hostLikeUrlMatch?.[1] ?? '/';
    const path = extractedPath || '/';

    if (basePath !== '/' && path.startsWith(basePath)) {
      const trimmedPath = path.slice(basePath.length - 1);
      return trimmedPath.startsWith('/') ? trimmedPath : `/${trimmedPath}`;
    }

    return path.startsWith('/') ? path : `/${path}`;
  }

  if (basePath !== '/' && rawUrl.startsWith(basePath)) {
    const trimmedPath = rawUrl.slice(basePath.length - 1);
    return trimmedPath.startsWith('/') ? trimmedPath : `/${trimmedPath}`;
  }

  return rawUrl.startsWith('/') ? rawUrl : `/${rawUrl}`;
}

function decodeSlugValue(rawSlug: string): string {
  try {
    return decodeURIComponent(rawSlug.replace(/\+/g, ' '));
  } catch {
    return rawSlug.replace(/\+/g, ' ');
  }
}

export function extractStorySlugFromWardPath(path: string): string | null {
  const cleanPath = path.trim();
  const [pathname, queryString = ''] = cleanPath.split('?', 2);
  const normalizedPathname = pathname.replace(/\/+$/g, '');
  const basePath = getBasePath().replace(/\/+$/g, '');
  const storyPagePath = `${basePath === '' ? '' : basePath}/storyblok-page`;

  if (normalizedPathname !== '/storyblok-page' && normalizedPathname !== storyPagePath) {
    return null;
  }

  const params = new URLSearchParams(queryString);
  const slugValue = params.get('slug');
  if (!slugValue) {
    return null;
  }

  const decodedSlug = decodeSlugValue(slugValue);
  const normalizedSlug = decodedSlug.replace(/^\/+|\/+$/g, '');
  return normalizedSlug || null;
}

function extractStoryblokSlug(rawUrl: string): string | null {
  const candidate = HTTP_URL_REGEX.test(rawUrl)
    ? (rawUrl.match(/^https?:\/\/[^/]+(\/.*)?$/i)?.[1] ?? '/')
    : rawUrl;

  const querySlugMatch = candidate.match(/[?&]slug=([^&#]+)/i);
  if (querySlugMatch?.[1]) {
    const decodedSlug = decodeSlugValue(querySlugMatch[1]);
    const cleanQuerySlug = decodedSlug.replace(/^\/+|\/+$/g, '');
    if (cleanQuerySlug) {
      return cleanQuerySlug;
    }
  }

  const cleanSlug = candidate.split(/[?#]/, 1)[0].replace(/^\/+|\/+$/g, '');
  return cleanSlug || null;
}

function normalizeStoryblokUrl(rawValue: string): string {
  let url = rawValue.trim();

  const markdownLinkMatch = url.match(/^\[[^\]]*\]\(([^)]+)\)$/);
  if (markdownLinkMatch?.[1]) {
    url = markdownLinkMatch[1].trim();
  }

  const bracketOnlyMatch = url.match(/^\[(.+)\]$/);
  if (bracketOnlyMatch?.[1]) {
    url = bracketOnlyMatch[1].trim();
  }

  if (url.startsWith('<') && url.endsWith('>')) {
    url = url.slice(1, -1).trim();
  }

  return url;
}

export function resolveStoryblokLink(
  link?: StoryblokLinkBlok | null,
  linkType?: string
): StoryblokNavigationTarget | null {
  if (!link) {
    return null;
  }

  const rawUrlCandidate = link.cached_url?.trim() || link.url?.trim() || '';
  if (!rawUrlCandidate) {
    return null;
  }

  const rawUrl = normalizeStoryblokUrl(rawUrlCandidate);
  const normalizedLinkType = linkType?.trim().toLowerCase();
  const normalizedStoryblokLinkType = link.linktype?.trim().toLowerCase();

  if (!rawUrl || rawUrl === '#') {
    return null;
  }

  if (normalizedStoryblokLinkType === 'email') {
    return {
      type: 'external',
      href: rawUrl.startsWith('mailto:') ? rawUrl : `mailto:${rawUrl}`,
    };
  }

  if (normalizedLinkType === LinkType.STORYBLOK || normalizedStoryblokLinkType === 'story') {
    const slug = extractStoryblokSlug(rawUrl);
    return slug ? { type: 'storyblok', slug } : null;
  }

  if (normalizedLinkType === LinkType.WARD) {
    const normalizedPath = normalizeInternalPath(rawUrl);
    const storySlug = extractStorySlugFromWardPath(normalizedPath);
    if (storySlug) {
      return { type: 'storyblok', slug: storySlug };
    }
    return { type: 'internal', path: normalizedPath };
  }

  if (normalizedLinkType === LinkType.EXTERNAL) {
    if (rawUrl.startsWith('//')) {
      return { type: 'external', href: `https:${rawUrl}` };
    }
    if (URL_SCHEME_REGEX.test(rawUrl)) {
      return { type: 'external', href: rawUrl };
    }
    return { type: 'external', href: `https://${rawUrl}` };
  }

  if (rawUrl.startsWith('//')) {
    return { type: 'external', href: `https:${rawUrl}` };
  }

  if (URL_SCHEME_REGEX.test(rawUrl)) {
    return { type: 'external', href: rawUrl };
  }

  if (normalizedStoryblokLinkType === 'external' || normalizedStoryblokLinkType === 'url') {
    return { type: 'external', href: `https://${rawUrl}` };
  }

  return { type: 'internal', path: normalizeInternalPath(rawUrl) };
}
